import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import {
  ChatAction,
  ChatActivityStatus,
  ChatAttachmentRef,
  ChatConfirmation,
  ChatConversationApiRecord,
  ChatFile,
  ChatFollowUp,
  ChatMessagePayload,
  ChatReportJob,
  ChatSource,
  ChatbotIaService,
} from './chatbot-ia.service';
import { UserService } from '../user.service';
import { ChatMention } from '../chatbot-ia/chatbot-slash-commands';

export interface StoredChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  displayText?: string;
  actions: ChatAction[];
  confirmations: ChatConfirmation[];
  process?: ChatActivityStatus[];
  reports?: ChatReportJob[];
  files?: ChatFile[];
  attachments?: ChatAttachmentRef[];
  mentions?: ChatMention[];
  sources?: ChatSource[];
  followUps?: ChatFollowUp[];
  revisions?: string[];
  revisionIndex?: number;
  interrupted?: boolean;
  createdAt: number;
  thinkingDurationSeconds?: number;
}

export interface ChatConversationRecord {
  id: string;
  title: string;
  messages: StoredChatMessage[];
  createdAt: number;
  updatedAt: number;
  unread?: boolean;
  parentConversationId?: string | null;
  branchedFromMessageId?: string | null;
  preview?: string | null;
}

interface ChatConversationsStore {
  version: 1;
  activeConversationId: string | null;
  conversations: ChatConversationRecord[];
}

const STORE_VERSION = 1 as const;
const MAX_CONVERSATIONS_GUEST = 50;
const MAX_CONVERSATIONS_USER = 200;
const STORAGE_PREFIX = 'wosi-chat-conversations:v1';
const IMPORTED_PREFIX = 'wosi-chat-imported:v1';

@Injectable({ providedIn: 'root' })
export class ChatbotConversationsService {
  private platformId = inject(PLATFORM_ID);
  private userService = inject(UserService);
  private chatbotApi = inject(ChatbotIaService);

  private memoryCache: ChatConversationsStore | null = null;
  private scopeKey = 'anon';
  private hydrating = false;

  refreshScope(): void {
    const userId = this.userService.getUser()?.id;
    const nextScope = userId ? `user:${userId}` : 'anon';
    if (nextScope === this.scopeKey && this.memoryCache) return;
    this.scopeKey = nextScope;
    this.memoryCache = this.readStore();
  }

  loadStore(): ChatConversationsStore {
    this.refreshScope();
    return this.cloneStore(this.memoryCache!);
  }

  saveStore(store: ChatConversationsStore): void {
    const normalized = this.normalizeStore(store);
    this.memoryCache = normalized;
    this.writeStore(normalized);
  }

  createConversation(title = 'Nova conversa'): ChatConversationRecord {
    const now = Date.now();
    return {
      id: this.newId(),
      title,
      messages: [],
      createdAt: now,
      updatedAt: now,
      unread: false,
    };
  }

  hasSameContent(
    current: ChatConversationRecord | undefined,
    next: Pick<ChatConversationRecord, 'title' | 'messages'>
  ): boolean {
    if (!current) return false;
    if (current.title !== next.title) return false;
    if (current.messages.length !== next.messages.length) return false;

    return current.messages.every((message, index) => {
      const other = next.messages[index];
      return (
        message.id === other.id &&
        message.role === other.role &&
        message.text === other.text &&
        message.createdAt === other.createdAt
      );
    });
  }

  upsertConversation(
    store: ChatConversationsStore,
    conversation: ChatConversationRecord,
    options?: { activate?: boolean }
  ): ChatConversationsStore {
    const conversations = [...store.conversations];
    const index = conversations.findIndex((item) => item.id === conversation.id);
    if (index >= 0) {
      conversations[index] = conversation;
    } else {
      conversations.unshift(conversation);
    }

    conversations.sort((a, b) => b.updatedAt - a.updatedAt);
    const trimmed = conversations.slice(0, this.maxConversations());
    const activate = options?.activate !== false;

    return {
      version: STORE_VERSION,
      activeConversationId: activate
        ? conversation.id
        : store.activeConversationId,
      conversations: trimmed,
    };
  }

  deleteConversation(store: ChatConversationsStore, conversationId: string): ChatConversationsStore {
    const conversations = store.conversations.filter((item) => item.id !== conversationId);
    let activeConversationId = store.activeConversationId;
    if (activeConversationId === conversationId) {
      activeConversationId = conversations[0]?.id ?? null;
    }

    return {
      version: STORE_VERSION,
      activeConversationId,
      conversations,
    };
  }

  toPayload(messages: StoredChatMessage[]): ChatMessagePayload[] {
    return messages.map((message) => ({
      role: message.role,
      text: message.text,
      ...(message.attachments?.length
        ? { attachments: message.attachments }
        : {}),
      ...(message.role === 'user' && message.mentions?.length
        ? {
            mentions: message.mentions.map((mention) => ({
              type: mention.type,
              id: mention.id,
              ...(mention.name ? { name: mention.name } : {}),
            })),
          }
        : {}),
      ...(message.role === 'assistant' && message.actions?.length
        ? {
            actions: message.actions.map((action) => ({
              label: action.label,
              url: action.url,
            })),
          }
        : {}),
    }));
  }

  buildConversationPreview(conversation: ChatConversationRecord): string {
    if (!conversation.messages.length) {
      return 'Sem mensagens ainda';
    }

    const last = [...conversation.messages]
      .reverse()
      .find((message) => this.messagePlainText(message).length > 0);
    if (!last) return 'Sem mensagens ainda';

    return this.truncatePreview(this.stripMarkdown(this.messagePlainText(last)));
  }

  normalizeSearchText(value: string): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .toLowerCase()
      .trim();
  }

  matchesConversationSearch(
    conversation: ChatConversationRecord,
    rawQuery: string
  ): boolean {
    const query = this.normalizeSearchText(rawQuery);
    if (!query) return true;

    const tokens = query.split(/\s+/).filter(Boolean);
    if (!tokens.length) return true;

    const haystack = this.normalizeSearchText(
      [
        conversation.title,
        this.buildConversationPreview(conversation),
        ...conversation.messages.map((message) => this.messagePlainText(message)),
      ].join(' ')
    );

    return tokens.every((token) => haystack.includes(token));
  }

  formatRelativeDate(timestamp: number): string {
    const diffMs = Date.now() - timestamp;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Agora';
    if (diffMin < 60) return `${diffMin} min`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours} h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `${diffDays} dias`;
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
    }).format(new Date(timestamp));
  }

  private normalizeStore(store: ChatConversationsStore): ChatConversationsStore {
    const conversations = [...(store.conversations ?? [])]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, this.maxConversations());

    let activeConversationId = store.activeConversationId;
    if (!activeConversationId || !conversations.some((item) => item.id === activeConversationId)) {
      activeConversationId = conversations[0]?.id ?? null;
    }

    return {
      version: STORE_VERSION,
      activeConversationId,
      conversations,
    };
  }

  private readStore(): ChatConversationsStore {
    if (!isPlatformBrowser(this.platformId)) {
      return this.emptyStore();
    }

    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) return this.emptyStore();
      const parsed = JSON.parse(raw) as ChatConversationsStore;
      if (parsed?.version !== STORE_VERSION || !Array.isArray(parsed.conversations)) {
        return this.emptyStore();
      }
      return this.normalizeStore(parsed);
    } catch {
      return this.emptyStore();
    }
  }

  private writeStore(store: ChatConversationsStore): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(store));
    } catch {
      // quota exceeded — fail silently
    }
  }

  private storageKey(): string {
    return `${STORAGE_PREFIX}:${this.scopeKey}`;
  }

  private emptyStore(): ChatConversationsStore {
    return {
      version: STORE_VERSION,
      activeConversationId: null,
      conversations: [],
    };
  }

  private cloneStore(store: ChatConversationsStore): ChatConversationsStore {
    return JSON.parse(JSON.stringify(store)) as ChatConversationsStore;
  }

  private messagePlainText(message: StoredChatMessage): string {
    if (message.role === 'user' && message.displayText?.trim()) {
      return message.displayText.trim();
    }
    return String(message.text ?? '').trim();
  }

  private stripMarkdown(text: string): string {
    return text
      .replace(/^CONFIRMO:\s*/i, '')
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/[*_~`>#]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private truncatePreview(text: string, max = 72): string {
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1).trim()}…`;
  }

  async hydrateFromRemote(): Promise<ChatConversationsStore> {
    if (!isPlatformBrowser(this.platformId) || this.hydrating) {
      return this.loadStore();
    }

    this.hydrating = true;
    this.refreshScope();
    const local = this.loadStore();

    try {
      if (this.userService.getUser()?.id) {
        await firstValueFrom(this.chatbotApi.claimConversations());
      }

      const remote = await firstValueFrom(this.chatbotApi.listConversations());
      let conversations = (remote.conversations ?? []).map((item) =>
        this.fromApiRecord(item)
      );

      if (!conversations.length && local.conversations.length && !this.alreadyImported()) {
        const imported = await firstValueFrom(
          this.chatbotApi.importConversations(
            local.conversations.map((item) => this.toApiRecord(item))
          )
        );
        conversations = (imported.conversations ?? []).map((item) =>
          this.fromApiRecord(item)
        );
        this.markImported();
      } else if (conversations.length) {
        this.markImported();
      }

      const store: ChatConversationsStore = this.normalizeStore({
        version: STORE_VERSION,
        activeConversationId: local.activeConversationId,
        conversations,
      });
      this.saveStore(store);
      return store;
    } catch {
      return local;
    } finally {
      this.hydrating = false;
    }
  }

  syncConversation(record: ChatConversationRecord): void {
    if (!isPlatformBrowser(this.platformId)) return;
    void firstValueFrom(this.chatbotApi.upsertConversation(this.toApiRecord(record))).catch(
      () => undefined
    );
  }

  syncDelete(conversationId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    void firstValueFrom(this.chatbotApi.deleteRemoteConversation(conversationId)).catch(
      () => undefined
    );
  }

  async branchFrom(
    conversationId: string,
    messageId: string,
    title?: string
  ): Promise<ChatConversationRecord | null> {
    try {
      const response = await firstValueFrom(
        this.chatbotApi.branchConversation(conversationId, { messageId, title })
      );
      return response?.conversation
        ? this.fromApiRecord(response.conversation)
        : null;
    } catch {
      return null;
    }
  }

  private maxConversations(): number {
    return this.userService.getUser()?.id
      ? MAX_CONVERSATIONS_USER
      : MAX_CONVERSATIONS_GUEST;
  }

  private importedKey(): string {
    return `${IMPORTED_PREFIX}:${this.scopeKey}`;
  }

  private alreadyImported(): boolean {
    try {
      return localStorage.getItem(this.importedKey()) === '1';
    } catch {
      return false;
    }
  }

  private markImported(): void {
    try {
      localStorage.setItem(this.importedKey(), '1');
    } catch {
      // ignore
    }
  }

  private toApiRecord(record: ChatConversationRecord): ChatConversationApiRecord {
    return {
      id: record.id,
      title: record.title,
      preview: record.preview ?? null,
      unread: record.unread,
      parentConversationId: record.parentConversationId ?? null,
      branchedFromMessageId: record.branchedFromMessageId ?? null,
      messages: record.messages,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  }

  fromApiRecord(record: ChatConversationApiRecord): ChatConversationRecord {
    return {
      id: record.id,
      title: record.title,
      preview: record.preview,
      unread: record.unread,
      parentConversationId: record.parentConversationId,
      branchedFromMessageId: record.branchedFromMessageId,
      messages: (record.messages ?? []).map((message) => ({
        id: message.id,
        role: message.role,
        text: message.text,
        displayText: message.displayText,
        actions: message.actions ?? [],
        confirmations: message.confirmations ?? [],
        process: message.process,
        reports: message.reports,
        files: message.files,
        attachments: message.attachments,
        mentions: message.mentions as ChatMention[] | undefined,
        sources: message.sources,
        followUps: message.followUps,
        revisions: message.revisions,
        revisionIndex: message.revisionIndex,
        interrupted: message.interrupted,
        createdAt: message.createdAt,
      })),
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  }

  private newId(): string {
    const uuid = globalThis.crypto?.randomUUID?.();
    return uuid ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}
